import { useState, useEffect } from 'react';
import '../css/application.css';
import {
    Button,
    Box,
    TextField,
    Divider,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
} from '@mui/material';
import Mapgl from '../components/mapboxEvacAccess/mapgl';
import SplitButton from '../components/tools/splitButton';

import {
    HSPA_API_BASE,
    GET_ACCESSIBILITY_DATA,
    GET_SHORTENED_DATA,
    NAVIGATE_LA_LINK,
    HSPA_TITLE,
} from '../config/constants';

/*
Accessibility Page

* Handles state of currently selected section ID
* Handles state of evacuation path data for selecte section ID
* Handles state of total road data
*/
function Accessibility({ dimensions }) {
    const [sectionID, setSectionID] = useState(3512700);
    const [geoData, setGeoData] = useState([]);
    const [path, setPath] = useState([]);
    const [pathColor, setPathColor] = useState([]);
    const [sectionData, setSectionData] = useState([]);
    const [dataSourceType, setDataSourceType] = useState('incoming');
    const [errorMessage, setErrorMessage] = useState('');

    // Gets latest evacuation data for the selected section ID
    useEffect(() => {
        fetch(
            HSPA_API_BASE +
                GET_ACCESSIBILITY_DATA +
                `?id=` +
                String(sectionID) +
                '&dataSource=' +
                dataSourceType
        )
            .then((response) => response.json())
            .then(
                (result) => {
                    setPath(result.pathData);
                    setPathColor(result.colorData);
                    setSectionData(result);
                },
                (error) => {
                    console.log(error);
                    setErrorMessage('No accessibility information for this segment');
                }
            )
            .catch((error) => {
                console.error('There was a problem fetching geoJson Data', error);
            });
    }, [sectionID, dataSourceType]);

    // Gets shortened hillside data and saves it to render in the map
    useEffect(() => {
        fetch(HSPA_API_BASE + GET_SHORTENED_DATA)
            .then((response) => response.json())
            .then((res) => {
                setGeoData(res);
            })
            .catch((error) => {
                console.error('There was a problem fetching goeJson Data', error);
            });
    }, []);

    // Function to update road if clicked
    function roadClicked(roadData) {
        if (roadData.length > 0) {
            setSectionID(roadData[0].properties.SECT_ID);
            setErrorMessage('');
        }
    }

    const handleDataSourceTypeChange = (e) => {
        setDataSourceType(e.target.value);
    };
    return (
        <div className="application">
            <Box sx={{ display: 'flex' }}>
                <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                    <div className="intro">
                        <h1>{HSPA_TITLE} Accessibility</h1>
                    </div>
                    <div className="col">
                        <div className="toolbar">
                            <div className="accessibility-data-source">
                                <FormControl fullWidth>
                                    <InputLabel id="access-data-source-select">
                                        Data Source Type
                                    </InputLabel>
                                    <Select
                                        labelId="access-data-source-select"
                                        value={dataSourceType}
                                        label="Data Source Type"
                                        onChange={handleDataSourceTypeChange}
                                    >
                                        <MenuItem value="incoming">Incoming Path Data</MenuItem>
                                        <MenuItem value="outgoing">Outgoing Path Data</MenuItem>
                                    </Select>
                                </FormControl>
                            </div>
                            <h2 className="navla">
                                Explore Section IDs:
                                <Button target="_blank" href={NAVIGATE_LA_LINK} variant="contained">
                                    Navigate LA
                                </Button>
                            </h2>
                        </div>
                        <div className="row">
                            <div className="col">
                                <h2>Select Section ID:</h2>
                                <div className="sectionInput">
                                    <TextField
                                        type="number"
                                        value={sectionID}
                                        onChange={(e) => setSectionID(e.target.value)}
                                    ></TextField>
                                </div>
                                <div className="divBar">
                                    <Divider variant="middle" />
                                </div>
                                <div className="evacDataContainer">
                                    {errorMessage ? (
                                        <h2 style={{ color: 'black' }}>{errorMessage}</h2>
                                    ) : sectionData.sectionID == null ? (
                                        <h2>Data Loading...</h2>
                                    ) : sectionData.Error == null ? (
                                        <div>
                                            <h2>Accessibility Data:</h2>
                                            <ul>
                                                <li className="evacData">
                                                    Section ID: {sectionData.sectionID}
                                                </li>
                                                <li className="evacData">
                                                    Street Name: {sectionData.streetName}
                                                </li>
                                                <li className="evacData">
                                                    Council District:{' '}
                                                    {parseInt(sectionData.district)}
                                                </li>
                                                <li className="evacData">
                                                    Total Length to access [feet]:{' '}
                                                    {Number.parseFloat(
                                                        sectionData.totalLengthToAccess
                                                    ).toFixed(2)}
                                                </li>
                                                <li className="evacData">
                                                    Length Roads above 20 ft Width [feet]:{' '}
                                                    {Number.parseFloat(
                                                        sectionData.lengthRoadsAbove20ft
                                                    ).toFixed(2)}
                                                </li>
                                                <li className="evacData">
                                                    Length Roads below 20 ft Width [feet]:{' '}
                                                    {Number.parseFloat(
                                                        sectionData.lengthRoadsBelow20ft
                                                    ).toFixed(2)}
                                                </li>
                                                <li className="evacData">
                                                    Length Roads with unknown Width [feet]:{' '}
                                                    {Number.parseFloat(
                                                        sectionData.lengthRoadsWithUnknownWidth
                                                    ).toFixed(2)}
                                                </li>
                                            </ul>
                                            <div className="divBar">
                                                <Divider variant="middle" />
                                            </div>
                                            <h2>Download Paths:</h2>
                                            <div className="evacData">
                                                <SplitButton
                                                    downloadData={path}
                                                    fileName="Path Data"
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <h2>{sectionData.Error}</h2>
                                    )}
                                </div>
                            </div>
                            <div className="col">
                                <Mapgl
                                    geoData={[
                                        {
                                            data: geoData,
                                            color: ['white'],
                                            id: 'highlight',
                                            width: 2.5,
                                            displayWidth: 6,
                                        },
                                        { data: path, color: pathColor, id: 'path', width: 4 },
                                    ]}
                                    width={(dimensions.width * 75) / 100}
                                    height={600}
                                    mapZoom={11}
                                    initLat={34.1017}
                                    initLng={-118.4438}
                                    polyControl={false}
                                    controls={['full']}
                                    callback={roadClicked}
                                />
                            </div>
                        </div>
                    </div>
                </Box>
            </Box>
        </div>
    );
}

export default Accessibility;
